import React from "react";
import { FaWhatsapp } from "react-icons/fa";
import {
  Truck,
  Clock,
  Layers,
  Stethoscope,
  ShieldCheck,
  Pill,
  HeartPulse,
  Soup,
  Bone,
  ShieldPlus,
  Brain,
  Wind,
  Sparkles,
  ArrowRight,
  BadgeCheck,
  FlaskConical,
} from "lucide-react";
import { useLang } from "../lib/LangContext";
import { WHATSAPP_NUMBER, translations } from "../lib/mock";

const BENEFIT_ICONS = {
  truck: Truck,
  clock: Clock,
  layers: Layers,
  stethoscope: Stethoscope,
};

const CATEGORY_ICONS = {
  pill: Pill,
  "heart-pulse": HeartPulse,
  stomach: Soup,
  bone: Bone,
  "shield-plus": ShieldPlus,
  brain: Brain,
  wind: Wind,
  sparkles: Sparkles,
};

const buildWa = (msg) => `https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`;

export const Hospitals = () => {
  const { t } = useLang();
  const h = t.hospitals || translations.en.hospitals;

  return (
    <section
      id="hospitals"
      className="relative bg-lokam-forest text-lokam-cream overflow-hidden"
    >
      {/* Grain texture */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #FFFDF9 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative px-6 md:px-12 lg:px-20 max-w-[1400px] mx-auto py-20 md:py-28">
        {/* Head */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-lokam-tangerine/30 bg-lokam-tangerine/10 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-lokam-tangerine">
            <BadgeCheck className="h-3.5 w-3.5" />
            {h.eyebrow}
          </div>
          <h2 className="mt-5 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-balance">
            {h.title}
          </h2>
          <p className="mt-5 text-base md:text-lg text-lokam-cream/75 leading-relaxed">
            {h.subtitle}
          </p>
        </div>

        {/* Benefits — 2x2 grid */}
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
          {h.benefits.map((b, i) => {
            const Icon = BENEFIT_ICONS[b.icon] || Truck;
            return (
              <div
                key={i}
                data-testid={`hospitals-benefit-${i}`}
                className="rounded-2xl bg-lokam-forest-soft/40 border border-white/10 p-6 md:p-7 hover:bg-lokam-forest-soft/60 transition-colors"
              >
                <div className="h-11 w-11 rounded-xl bg-lokam-tangerine/15 text-lokam-tangerine flex items-center justify-center">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="mt-4 font-display font-semibold text-lg md:text-xl text-lokam-cream leading-tight">
                  {b.title}
                </div>
                <p className="mt-2 text-sm text-lokam-cream/70 leading-relaxed">
                  {b.body}
                </p>
              </div>
            );
          })}
        </div>

        {/* Trust badge strip */}
        <div
          data-testid="hospitals-trust-strip"
          className="mt-10 rounded-2xl bg-lokam-cream/5 border border-white/10 px-5 md:px-7 py-5"
        >
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-lokam-tangerine">
            <ShieldCheck className="h-3.5 w-3.5" />
            {h.trust_strip_title}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {h.trust_badges.map((badge, i) => (
              <span
                key={i}
                data-testid={`hospitals-badge-${i}`}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-lokam-forest-soft/30 px-3.5 py-2 text-xs md:text-sm font-semibold text-lokam-cream"
              >
                <FlaskConical className="h-3.5 w-3.5 text-lokam-tangerine" />
                {badge}
              </span>
            ))}
          </div>
        </div>

        {/* Therapeutic categories */}
        <div className="mt-16">
          <h3 className="font-display font-semibold text-2xl md:text-3xl text-lokam-cream tracking-tight">
            {h.categories_title}
          </h3>
          <p className="mt-3 text-sm md:text-base text-lokam-cream/65 max-w-2xl">
            {h.categories_subtitle}
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {h.categories.map((c, i) => {
              const Icon = CATEGORY_ICONS[c.icon] || Pill;
              return (
                <div
                  key={i}
                  data-testid={`hospitals-category-${i}`}
                  className="group rounded-2xl bg-lokam-cream/5 border border-white/10 p-5 md:p-6 hover:bg-lokam-cream/10 hover:border-lokam-tangerine/30 transition-colors flex items-start gap-4"
                >
                  <div className="h-10 w-10 rounded-lg bg-lokam-tangerine/15 text-lokam-tangerine flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="font-display font-semibold text-base md:text-lg text-lokam-cream leading-snug">
                    {c.name}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTAs */}
        <div className="mt-12 flex flex-wrap items-center gap-3">
          <a
            href={buildWa(h.cta_primary_msg)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hospitals-cta-primary"
            className="inline-flex items-center gap-2.5 rounded-full bg-lokam-tangerine hover:bg-lokam-tangerine-dark text-white px-5 py-4 text-sm md:text-base font-semibold shadow-[0_8px_28px_rgba(255,84,0,0.35)] transition-all hover:-translate-y-0.5"
          >
            <FaWhatsapp className="h-5 w-5" />
            {h.cta_primary}
          </a>
          <a
            href={buildWa(h.cta_secondary_msg)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hospitals-cta-secondary"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-lokam-tangerine hover:bg-lokam-tangerine/10 text-lokam-cream px-5 py-4 text-sm md:text-base font-semibold transition-colors"
          >
            {h.cta_secondary}
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Hospitals;
